import { useEffect, useState, useRef } from "react";
import { Plus, Pencil, Trash2, X, Download, ChevronLeft, Star, Crop, RefreshCcw, RotateCcw, RotateCw } from "lucide-react";
import api from "@/api";
import { formatINR } from "@/store/StoreContext";
import { toast } from "sonner";
import { imgT, DEFAULT_IMG_T } from "@/utils/img";

function ProductImageAdjust({ im, onChange, onClose }) {
  const t = { ...DEFAULT_IMG_T, ...(im.transform || {}) };
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const setT = (patch) => onChange({ ...t, ...patch });
  const onPointerDown = (e) => {
    dragRef.current = { sx: e.clientX, sy: e.clientY, ox: t.x, oy: t.y };
    e.target.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || !frameRef.current) return;
    const r = frameRef.current.getBoundingClientRect();
    setT({ x: Math.round(d.ox + ((e.clientX - d.sx) / r.width) * 100), y: Math.round(d.oy + ((e.clientY - d.sy) / r.height) * 100) });
  };
  const onPointerUp = () => { dragRef.current = null; };
  return (
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="pe-img-adjust-modal">
      <div className="bg-[#FAF8F5] w-full max-w-md p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl">Adjust Image</h3>
          <button onClick={onClose} aria-label="Close" data-testid="pe-adjust-close"><X size={18} /></button>
        </div>
        <div ref={frameRef} data-testid="pe-adjust-frame"
          className="relative w-full aspect-[3/4] bg-white border border-[#E5DEC9] overflow-hidden cursor-grab active:cursor-grabbing touch-none select-none"
          onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}>
          <img src={im.url} alt={im.alt || ""} draggable={false} style={imgT(t)} className="w-full h-full pointer-events-none" />
        </div>
        <p className="text-[10px] text-[#9a938a] mt-1.5">Drag the image to reposition · this preview matches the live product page</p>
        <div className="mt-4 space-y-4">
          <div>
            <p className="tq-overline mb-2">Fit</p>
            <div className="grid grid-cols-2 gap-1.5">
              {[["contain", "Contain / Fit Inside"], ["cover", "Cover / Fill"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setT({ fit: v })} data-testid={`pe-adjust-fit-${v}`}
                  className={`px-2 py-2 text-[11px] border ${t.fit === v ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9] bg-white"}`}>{l}</button>
              ))}
            </div>
            <p className="text-[10px] text-[#9a938a] mt-1">Contain = whole product visible (never cut) · Cover = fills frame (may crop edges)</p>
          </div>
          <div>
            <div className="flex justify-between items-center mb-1"><p className="tq-overline">Zoom</p><span className="text-[10px] text-[#8C7A5E]">{Math.round(t.zoom * 100)}%</span></div>
            <input type="range" min="0.5" max="3" step="0.05" value={t.zoom} onChange={(e) => setT({ zoom: Number(e.target.value) })} data-testid="pe-adjust-zoom" className="w-full accent-[#D4AF37]" />
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="tq-overline">Rotate</p>
            <div className="flex gap-1.5">
              <button type="button" onClick={() => setT({ rot: (t.rot - 90 + 360) % 360 })} aria-label="Rotate left" data-testid="pe-adjust-rot-left" className="p-2 border border-[#E5DEC9] bg-white"><RotateCcw size={14} /></button>
              <button type="button" onClick={() => setT({ rot: (t.rot + 90) % 360 })} aria-label="Rotate right" data-testid="pe-adjust-rot-right" className="p-2 border border-[#E5DEC9] bg-white"><RotateCw size={14} /></button>
            </div>
          </div>
          <div className="flex gap-2 pt-3 border-t border-[#E5DEC9]">
            <button type="button" onClick={() => onChange({ ...DEFAULT_IMG_T })} data-testid="pe-adjust-reset" className="tq-btn-outline flex-1 !py-2">Reset</button>
            <button type="button" onClick={onClose} data-testid="pe-adjust-done" className="tq-btn-dark flex-1 !py-2">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const EMPTY = { name: "", slug: "", sku: "", mrp: "", price: "", cost: "", gst: 5, hsn: "6403", category_slugs: [], collection_slugs: [], short_description: "", description: "", heel_type: "", heel_height: "", material: "", occasion: [], color: "", care: "", shipping_info: "", highlights: [], badges: [], images: [], variants: [], status: "draft", processing_extra: 0, seo: { title: "", description: "" } };
const BADGES = ["new", "bestseller", "trending", "handcrafted", "exclusive", "sale"];
const OCCASIONS = ["wedding", "bridal", "party", "festive", "casual", "office", "evening", "formal", "vacation"];

function Editor({ product, onClose, onSaved, categories, collections }) {
  const [f, setF] = useState(product || EMPTY);
  const [busy, setBusy] = useState(false);
  const [newImg, setNewImg] = useState("");
  const [uploading, setUploading] = useState(false);
  const imgFileRef = useRef(null);
  const replaceFileRef = useRef(null);
  const [replaceIdx, setReplaceIdx] = useState(null);
  const [adjustIdx, setAdjustIdx] = useState(null);
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggleArr = (k, v) => setF({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] });

  const addVariant = () => setF({ ...f, variants: [...f.variants, { sku: "", size: "", color: f.color, stock: 10, mrp: "", price: "", low_stock_threshold: 3 }] });
  const setVariant = (i, k, v) => { const vs = [...f.variants]; vs[i] = { ...vs[i], [k]: v }; setF({ ...f, variants: vs }); };
  const addImage = () => { if (!newImg.trim()) return; setF({ ...f, images: [...f.images, { url: newImg.trim(), alt: f.name }] }); setNewImg(""); };
  const uploadImages = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || uploading) return;
    setUploading(true);
    try {
      const added = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("alt", f.name || file.name);
        const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ url: data.url, alt: f.name || file.name });
      }
      setF((cur) => ({ ...cur, images: [...cur.images, ...added] }));
      toast.success(`${added.length} image(s) uploaded`);
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setUploading(false); if (imgFileRef.current) imgFileRef.current.value = ""; }
  };
  const moveImg = (i, dir) => { const imgs = [...f.images]; const j = i + dir; if (j < 0 || j >= imgs.length) return; [imgs[i], imgs[j]] = [imgs[j], imgs[i]]; setF({ ...f, images: imgs }); };
  const setFeatured = (i) => { if (i === 0) return; const imgs = [...f.images]; const [im] = imgs.splice(i, 1); imgs.unshift(im); setF({ ...f, images: imgs }); };
  const setImgTransform = (i, t) => { const imgs = [...f.images]; imgs[i] = { ...imgs[i], transform: t }; setF({ ...f, images: imgs }); };
  const replaceImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || replaceIdx === null) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("alt", f.name || file.name);
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const imgs = [...f.images];
      imgs[replaceIdx] = { ...imgs[replaceIdx], url: data.url };
      setF({ ...f, images: imgs });
      toast.success("Image replaced");
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setReplaceIdx(null); }
  };

  const save = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!f.name || !f.price || !f.mrp) { toast.error("Name, MRP and selling price are required"); return; }
    setBusy(true);
    try {
      const payload = { ...f, mrp: Number(f.mrp), price: Number(f.price), cost: Number(f.cost || 0) };
      if (product?.id) await api.put(`/admin/products/${product.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(product?.id ? "Product updated" : "Product created");
      onSaved();
    } catch (err) { toast.error(err.friendlyMessage); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 overflow-y-auto" data-testid="product-editor">
      <div className="min-h-screen bg-[#FAF8F5] max-w-4xl mx-auto my-0 lg:my-8 p-5 sm:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">{product?.id ? "Edit Product" : "New Product"}</h2>
          <button onClick={onClose} aria-label="Close" data-testid="editor-close"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="space-y-8">
          <section className="bg-white border border-[#E5DEC9] p-5 space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E]">Basics</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className="tq-label">Product Name *</label>
                <input required value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="pe-name" className="tq-input" /></div>
              <div><label className="tq-label">Slug</label>
                <input value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto from name" data-testid="pe-slug" className="tq-input" /></div>
              <div><label className="tq-label">Base SKU</label>
                <input value={f.sku} onChange={(e) => set("sku", e.target.value)} data-testid="pe-sku" className="tq-input" /></div>
              <div><label className="tq-label">MRP (₹) *</label>
                <input required type="number" min="0" value={f.mrp} onChange={(e) => set("mrp", e.target.value)} data-testid="pe-mrp" className="tq-input" /></div>
              <div><label className="tq-label">Selling Price (₹) *</label>
                <input required type="number" min="0" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="pe-price" className="tq-input" /></div>
              <div><label className="tq-label">Cost Price (₹)</label>
                <input type="number" min="0" value={f.cost} onChange={(e) => set("cost", e.target.value)} data-testid="pe-cost" className="tq-input" /></div>
              <div><label className="tq-label">HSN</label>
                <input value={f.hsn} onChange={(e) => set("hsn", e.target.value)} className="tq-input" /></div>
              <div><label className="tq-label">Status</label>
                <select value={f.status} onChange={(e) => set("status", e.target.value)} data-testid="pe-status" className="tq-input bg-white">
                  <option value="draft">Draft</option><option value="active">Active</option><option value="archived">Archived</option>
                </select></div>
              <div><label className="tq-label">Extra Processing Days</label>
                <input type="number" min="0" value={f.processing_extra} onChange={(e) => set("processing_extra", e.target.value)} className="tq-input" /></div>
            </div>
            <div><label className="tq-label">Short Description</label>
              <input value={f.short_description} onChange={(e) => set("short_description", e.target.value)} className="tq-input" /></div>
            <div><label className="tq-label">Full Description</label>
              <textarea rows={4} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="pe-description" className="tq-input" /></div>
          </section>

          <section className="bg-white border border-[#E5DEC9] p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-4">Categories & Collections</p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <p className="tq-label">Categories</p>
                <div className="flex flex-wrap gap-2">
                  {categories.map((c) => (
                    <button type="button" key={c.slug} onClick={() => toggleArr("category_slugs", c.slug)} data-testid={`pe-cat-${c.slug}`}
                      className={`px-3 py-1.5 text-xs border ${f.category_slugs.includes(c.slug) ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9]"}`}>{c.name}</button>
                  ))}
                </div>
              </div>
              <div>
                <p className="tq-label">Collections</p>
                <div className="flex flex-wrap gap-2">
                  {collections.map((c) => (
                    <button type="button" key={c.slug} onClick={() => toggleArr("collection_slugs", c.slug)} data-testid={`pe-coll-${c.slug}`}
                      className={`px-3 py-1.5 text-xs border ${f.collection_slugs.includes(c.slug) ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9]"}`}>{c.name}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div><label className="tq-label">Heel Type</label>
                <input value={f.heel_type} onChange={(e) => set("heel_type", e.target.value)} placeholder="Block Heel, Stiletto…" className="tq-input" /></div>
              <div><label className="tq-label">Heel Height</label>
                <input value={f.heel_height} onChange={(e) => set("heel_height", e.target.value)} placeholder="3 inch" className="tq-input" /></div>
              <div><label className="tq-label">Material</label>
                <input value={f.material} onChange={(e) => set("material", e.target.value)} className="tq-input" /></div>
              <div><label className="tq-label">Color</label>
                <input value={f.color} onChange={(e) => set("color", e.target.value)} className="tq-input" /></div>
            </div>
            <div className="mt-4">
              <p className="tq-label">Occasions</p>
              <div className="flex flex-wrap gap-2">
                {OCCASIONS.map((o) => (
                  <button type="button" key={o} onClick={() => toggleArr("occasion", o)}
                    className={`px-3 py-1.5 text-xs border capitalize ${f.occasion.includes(o) ? "bg-[#121212] text-white border-[#121212]" : "border-[#E5DEC9]"}`}>{o}</button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <p className="tq-label">Badges</p>
              <div className="flex flex-wrap gap-2">
                {BADGES.map((b) => (
                  <button type="button" key={b} onClick={() => toggleArr("badges", b)}
                    className={`px-3 py-1.5 text-xs border uppercase ${f.badges.includes(b) ? "bg-[#D4AF37] text-[#121212] border-[#D4AF37]" : "border-[#E5DEC9]"}`}>{b}</button>
                ))}
              </div>
            </div>
            <div className="mt-4"><label className="tq-label">Highlights (one per line)</label>
              <textarea rows={3} value={(f.highlights || []).join("\n")} onChange={(e) => set("highlights", e.target.value.split("\n").filter(Boolean))} className="tq-input" /></div>
            <div className="mt-4"><label className="tq-label">Care Instructions</label>
              <textarea rows={2} value={f.care} onChange={(e) => set("care", e.target.value)} className="tq-input" /></div>
          </section>

          <section className="bg-white border border-[#E5DEC9] p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-4">Images</p>
            <div className="flex flex-wrap gap-2 mb-4">
              <button type="button" onClick={() => imgFileRef.current?.click()} disabled={uploading} data-testid="pe-image-upload" className="tq-btn-dark !py-2.5">
                {uploading ? "Uploading…" : "Upload from Gallery"}
              </button>
              <input ref={imgFileRef} type="file" accept="image/*" multiple onChange={uploadImages} className="hidden" data-testid="pe-image-file" />
              <input value={newImg} onChange={(e) => setNewImg(e.target.value)} placeholder="…or paste image URL" data-testid="pe-image-url" className="tq-input flex-1 min-w-[200px]" />
              <button type="button" onClick={addImage} data-testid="pe-image-add" className="tq-btn-outline !py-2.5">Add URL</button>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
              {f.images.map((im, i) => (
                <div key={i} className="relative group border border-[#E5DEC9] bg-white" data-testid={`pe-image-${i}`}>
                  <div className="w-full aspect-[3/4] overflow-hidden bg-[#FAFAFA]">
                    <img src={im.url} alt={im.alt} style={imgT(im.transform)} className="w-full h-full" />
                  </div>
                  {i === 0 && <span className="absolute top-1 left-1 text-[8px] bg-[#D4AF37] px-1.5 py-0.5 font-bold">FEATURED</span>}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-wrap items-center justify-center gap-1 p-1 content-center">
                    <button type="button" onClick={() => moveImg(i, -1)} aria-label="Move left" data-testid={`pe-img-left-${i}`} className="text-white text-xs px-1">←</button>
                    <button type="button" onClick={() => setFeatured(i)} aria-label="Set as featured" data-testid={`pe-img-featured-${i}`} className="text-white"><Star size={13} /></button>
                    <button type="button" onClick={() => setAdjustIdx(i)} aria-label="Crop and adjust" data-testid={`pe-img-adjust-${i}`} className="text-white"><Crop size={13} /></button>
                    <button type="button" onClick={() => { setReplaceIdx(i); replaceFileRef.current?.click(); }} aria-label="Replace image" data-testid={`pe-img-replace-${i}`} className="text-white"><RefreshCcw size={13} /></button>
                    <button type="button" onClick={() => set("images", f.images.filter((_, j) => j !== i))} aria-label="Delete image" data-testid={`pe-img-del-${i}`} className="text-white"><Trash2 size={14} /></button>
                    <button type="button" onClick={() => moveImg(i, 1)} aria-label="Move right" data-testid={`pe-img-right-${i}`} className="text-white text-xs px-1">→</button>
                  </div>
                  <p className="absolute bottom-0 inset-x-0 text-center text-[8px] bg-white/85 uppercase tracking-wider pointer-events-none">{im.transform?.fit || "contain"}</p>
                </div>
              ))}
            </div>
            <input ref={replaceFileRef} type="file" accept="image/*" onChange={replaceImage} className="hidden" data-testid="pe-image-replace-file" />
            <p className="text-[10px] text-[#9a938a] mt-2">First image = featured. Hover an image to reorder, set featured, crop/reposition/zoom/rotate, replace or delete. Changes go live when you save the product.</p>
            {adjustIdx !== null && f.images[adjustIdx] && (
              <ProductImageAdjust im={f.images[adjustIdx]} onChange={(t) => setImgTransform(adjustIdx, t)} onClose={() => setAdjustIdx(null)} />
            )}
          </section>

          <section className="bg-white border border-[#E5DEC9] p-5">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E]">Variants (Size × SKU × Stock)</p>
              <div className="flex gap-2">
                <button type="button" onClick={() => {
                  const have = new Set(f.variants.map((v) => v.size));
                  const add = ["36", "37", "38", "39", "40", "41"].filter((s) => !have.has(s))
                    .map((s) => ({ sku: `${f.sku || "TF"}-${s}`, size: s, color: f.color, stock: 12, mrp: "", price: "", low_stock_threshold: 3, enabled: true }));
                  setF({ ...f, variants: [...f.variants, ...add].sort((a, b) => Number(a.size) - Number(b.size) || String(a.size).localeCompare(String(b.size))) });
                }} data-testid="pe-add-sizes-36-41" className="tq-btn-outline !py-2 !px-3">Add 36–41</button>
                <button type="button" onClick={addVariant} data-testid="pe-add-variant" className="tq-btn-outline !py-2 !px-3"><Plus size={13} /> Add</button>
              </div>
            </div>
            {f.variants.length === 0 && <p className="text-sm text-[#9a938a]">Add at least one variant with size & stock.</p>}
            <div className="space-y-3">
              {f.variants.map((v, i) => (
                <div key={i} className="grid grid-cols-3 sm:grid-cols-8 gap-2 items-center border-b border-[#F4EFE6] pb-3">
                  <input placeholder="Size" value={v.size} onChange={(e) => setVariant(i, "size", e.target.value)} data-testid={`pe-var-size-${i}`} className="tq-input !py-2" />
                  <input placeholder="SKU" value={v.sku} onChange={(e) => setVariant(i, "sku", e.target.value)} className="tq-input !py-2" />
                  <input placeholder="Color" value={v.color} onChange={(e) => setVariant(i, "color", e.target.value)} className="tq-input !py-2" />
                  <input type="number" placeholder="Stock" value={v.stock} onChange={(e) => setVariant(i, "stock", e.target.value)} data-testid={`pe-var-stock-${i}`} className="tq-input !py-2" />
                  <input type="number" placeholder="MRP*" value={v.mrp ?? ""} onChange={(e) => setVariant(i, "mrp", e.target.value)} className="tq-input !py-2" />
                  <input type="number" placeholder="Price*" value={v.price ?? ""} onChange={(e) => setVariant(i, "price", e.target.value)} className="tq-input !py-2" />
                  <label className="flex items-center gap-1.5 text-[11px] text-[#6E6D6A] whitespace-nowrap cursor-pointer">
                    <input type="checkbox" checked={v.enabled !== false} onChange={(e) => setVariant(i, "enabled", e.target.checked)} data-testid={`pe-var-enabled-${i}`} className="accent-[#D4AF37] w-4 h-4" />
                    Enabled
                  </label>
                  <button type="button" onClick={() => set("variants", f.variants.filter((_, j) => j !== i))} aria-label="Remove variant" className="text-[#8B1E21] justify-self-center"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-[#9a938a] mt-3">* Variant MRP/Price override product-level pricing when set. Stock 0 = shown as out of stock; disabled sizes are hidden from the storefront.</p>
          </section>

          <section className="bg-white border border-[#E5DEC9] p-5">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E] mb-1">Size Chart Override (optional)</p>
            <p className="text-[11px] text-[#9a938a] mb-3">Leave blank to use the global size chart (Store Settings → Size Chart). One row per line: <code>UK/IND, EU, Foot Length (cm)</code>.</p>
            <textarea rows={4} className="tq-input font-mono text-xs" data-testid="pe-size-chart"
              value={(f.size_chart_rows || []).map((r) => r.join(", ")).join("\n")}
              onChange={(e) => set("size_chart_rows", e.target.value.split("\n").map((l) => l.split(",").map((x) => x.trim())).filter((r) => r.length && r[0]))}
              placeholder={"3, 36, 22.5\n4, 37, 23.2"} />
          </section>

          <section className="bg-white border border-[#E5DEC9] p-5 space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8C7A5E]">SEO</p>
            <div><label className="tq-label">SEO Title</label>
              <input value={f.seo?.title || ""} onChange={(e) => set("seo", { ...f.seo, title: e.target.value })} className="tq-input" /></div>
            <div><label className="tq-label">Meta Description</label>
              <textarea rows={2} value={f.seo?.description || ""} onChange={(e) => set("seo", { ...f.seo, description: e.target.value })} className="tq-input" /></div>
          </section>

          <div className="flex gap-3 sticky bottom-0 bg-[#FAF8F5] py-4 border-t border-[#E5DEC9]">
            <button type="submit" disabled={busy} data-testid="pe-save" className="tq-btn-dark">{busy ? "Saving…" : product?.id ? "Update Product" : "Create Product"}</button>
            <button type="button" onClick={onClose} className="tq-btn-outline">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminProducts() {
  const [data, setData] = useState(null);
  const [categories, setCategories] = useState([]);
  const [collections, setCollections] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState([]);

  const load = async () => {
    const { data } = await api.get("/admin/products", { params: { q, status, page, limit: 15 } });
    setData(data);
  };
  useEffect(() => {
    load();
    api.get("/admin/categories").then(({ data }) => setCategories(data));
    api.get("/admin/collections").then(({ data }) => setCollections(data));
  }, [q, status, page]);

  const del = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };
  const bulk = async (action, value) => {
    if (!selected.length) { toast.info("Select products first"); return; }
    await api.post("/admin/products/bulk", { ids: selected, action, value });
    toast.success("Bulk update applied");
    setSelected([]);
    load();
  };

  return (
    <div data-testid="admin-products">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-serif text-3xl">Products</h1>
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/export/products`} onClick={(e) => { e.preventDefault(); api.get("/admin/export/products", { responseType: "blob" }).then((r) => { const u = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = u; a.download = "products.csv"; a.click(); }); }}
            data-testid="products-export-btn" className="tq-btn-outline !py-2.5"><Download size={14} /> Export CSV</a>
          <button onClick={() => setEditing(EMPTY)} data-testid="product-create-btn" className="tq-btn-dark !py-2.5"><Plus size={14} /> New Product</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search name / SKU…" data-testid="products-search" className="tq-input !py-2.5 max-w-xs" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} data-testid="products-status-filter" className="tq-input !py-2.5 !w-auto bg-white">
          <option value="">All statuses</option><option value="active">Active</option><option value="draft">Draft</option><option value="archived">Archived</option>
        </select>
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2 items-center bg-white border border-[#D4AF37] px-3 py-1" data-testid="products-bulk-bar">
            <span className="text-xs font-semibold">{selected.length} selected</span>
            <button onClick={() => bulk("status", "active")} data-testid="bulk-activate" className="text-xs text-[#1A3C34] font-semibold">Activate</button>
            <button onClick={() => bulk("status", "draft")} data-testid="bulk-draft" className="text-xs text-[#8C7A5E] font-semibold">Draft</button>
            <select defaultValue="" onChange={(e) => { const [mode, slug] = e.target.value.split("|"); if (slug) bulk(mode === "add" ? "collections_add" : "collections_remove", [slug]); e.target.value = ""; }} data-testid="bulk-collections" className="text-xs border border-[#E5DEC9] py-1 px-1.5 bg-white">
              <option value="">Collections…</option>
              {collections.map((c) => (
                <optgroup key={c.slug} label={c.name}>
                  <option value={`add|${c.slug}`}>+ Add to {c.name}</option>
                  <option value={`remove|${c.slug}`}>− Remove from {c.name}</option>
                </optgroup>
              ))}
            </select>
            <select defaultValue="" onChange={(e) => { const [mode, slug] = e.target.value.split("|"); if (slug) bulk(mode === "add" ? "category_add" : "category_remove", slug); e.target.value = ""; }} data-testid="bulk-category" className="text-xs border border-[#E5DEC9] py-1 px-1.5 bg-white">
              <option value="">Category…</option>
              {categories.map((c) => (
                <optgroup key={c.slug} label={c.name}>
                  <option value={`add|${c.slug}`}>+ Set {c.name}</option>
                  <option value={`remove|${c.slug}`}>− Remove {c.name}</option>
                </optgroup>
              ))}
            </select>
            <button onClick={() => { const t = prompt("Tags to ADD (comma separated):"); if (t) bulk("tags_add", t); }} data-testid="bulk-tags-add" className="text-xs font-semibold">+ Tags</button>
            <button onClick={() => { const t = prompt("Tags to REMOVE (comma separated):"); if (t) bulk("tags_remove", t); }} data-testid="bulk-tags-remove" className="text-xs font-semibold">− Tags</button>
            <button onClick={() => { const pct = prompt("Discount % off MRP (e.g. 20 sets price = MRP − 20%)"); if (pct) bulk("discount_percent", pct); }} data-testid="bulk-discount" className="text-xs font-semibold text-[#1A3C34]">Discount %</button>
            <button onClick={() => { const pct = prompt("Change prices by % (e.g. 10 or -15)"); if (pct) bulk("price_percent", pct); }} data-testid="bulk-price" className="text-xs font-semibold">Price %</button>
            <button onClick={() => { if (window.confirm(`Remove discount on ${selected.length} product(s)? (price = MRP)`)) bulk("discount_clear"); }} data-testid="bulk-discount-clear" className="text-xs font-semibold">Clear Discount</button>
            <button onClick={() => bulk("delete")} data-testid="bulk-delete" className="text-xs text-[#8B1E21] font-semibold">Delete</button>
          </div>
        )}
      </div>
      {!data ? <div className="h-64 bg-white animate-pulse" /> : (
        <div className="bg-white border border-[#E5DEC9] overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-[#8C7A5E] border-b border-[#E5DEC9]">
                <th className="p-3 w-8"><input type="checkbox" aria-label="Select all" onChange={(e) => setSelected(e.target.checked ? data.products.map((p) => p.id) : [])} checked={selected.length === data.products.length && data.products.length > 0} className="accent-[#D4AF37]" /></th>
                <th className="p-3">Product</th><th className="p-3">Price</th><th className="p-3">Stock</th><th className="p-3">Status</th><th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((p) => {
                const stock = (p.variants || []).reduce((s, v) => s + (v.stock || 0), 0);
                return (
                  <tr key={p.id} className="border-b border-[#F4EFE6] hover:bg-[#FAF8F5]" data-testid={`product-row-${p.slug}`}>
                    <td className="p-3"><input type="checkbox" aria-label={`Select ${p.name}`} checked={selected.includes(p.id)} onChange={(e) => setSelected(e.target.checked ? [...selected, p.id] : selected.filter((x) => x !== p.id))} className="accent-[#D4AF37]" /></td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {p.images?.[0]?.url && <img src={p.images[0].url} alt="" className="w-10 h-12 object-cover bg-[#F4EFE6]" />}
                        <div><p className="font-medium">{p.name}</p><p className="text-xs text-[#9a938a]">{p.sku} · {(p.variants || []).length} variants</p></div>
                      </div>
                    </td>
                    <td className="p-3"><span className="font-semibold">{formatINR(p.price)}</span>{p.mrp > p.price && <span className="text-xs text-[#9a938a] line-through ml-2">{formatINR(p.mrp)}</span>}</td>
                    <td className="p-3"><span className={stock <= 5 ? "text-[#8B1E21] font-semibold" : ""}>{stock}</span></td>
                    <td className="p-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${p.status === "active" ? "bg-[#1A3C34]/10 text-[#1A3C34]" : "bg-[#F4EFE6] text-[#8C7A5E]"}`}>{p.status}</span></td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button onClick={() => setEditing(p)} aria-label="Edit" data-testid={`product-edit-${p.slug}`} className="p-1.5 hover:text-[#C5A059]"><Pencil size={15} /></button>
                        <button onClick={() => del(p.id)} aria-label="Delete" data-testid={`product-delete-${p.slug}`} className="p-1.5 hover:text-[#8B1E21]"><Trash2 size={15} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data.pages > 1 && (
            <div className="flex items-center justify-between p-4 text-sm">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="tq-btn-outline !py-2 !px-3 disabled:opacity-40"><ChevronLeft size={14} /> Prev</button>
              <span className="text-xs text-[#8C7A5E]">Page {data.page} of {data.pages} · {data.total} products</span>
              <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="tq-btn-outline !py-2 !px-3 disabled:opacity-40">Next <ChevronLeft size={14} className="rotate-180" /></button>
            </div>
          )}
        </div>
      )}
      {editing && <Editor product={editing.id ? editing : null} categories={categories} collections={collections}
        onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
